import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

// Disparado desde el menú de cuenta o la página de ayuda para abrir el diálogo
// sin acoplar esos componentes al estado del layout.
export const OPEN_SHORTCUTS_EVENT = "academy:open-shortcuts"

const STORAGE_KEY = "academy:shortcuts-enabled"

export const SHORTCUTS: { keys: string[]; label: string; studentOnly?: boolean }[] = [
  { keys: ["?"], label: "Ver atajos de teclado" },
  { keys: ["g", "i"], label: "Ir a Inicio" },
  { keys: ["g", "m"], label: "Ir a Mis cursos", studentOnly: true },
  { keys: ["g", "a"], label: "Ir a Ayuda" },
]

const GO_TARGETS: Record<string, { to: string; studentOnly?: boolean }> = {
  i: { to: "/dashboard" },
  m: { to: "/mis-cursos", studentOnly: true },
  a: { to: "/ayuda" },
}

const SEQUENCE_TIMEOUT_MS = 1000

// Superficies que atrapan el foco o ya tienen su propio teclado (WCAG 2.1.4):
// diálogos (incl. el modal de Calendly), menús y listas desplegables.
const BLOCKING_SURFACES =
  '[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"],.calendly-overlay'

function isEditable(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable || target.closest('[contenteditable="true"]')) return true
  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
}

function readEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "false"
  } catch {
    return true
  }
}

/**
 * Atajos globales. `active` indica si el layout los monta (sesión iniciada);
 * `enabled` es la preferencia del usuario, persistida, para poder apagarlos.
 */
export function useKeyboardShortcuts(active: boolean, isStudent = true) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [enabled, setEnabledState] = useState(readEnabled)
  const waitingForTarget = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value)
    try {
      localStorage.setItem(STORAGE_KEY, String(value))
    } catch {
      // Sin almacenamiento: la preferencia vale solo para esta pestaña.
    }
  }, [])

  const clearSequence = useCallback(() => {
    waitingForTarget.current = false
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }, [])

  useEffect(() => {
    const onOpenRequest = () => setOpen(true)
    window.addEventListener(OPEN_SHORTCUTS_EVENT, onOpenRequest)
    return () => window.removeEventListener(OPEN_SHORTCUTS_EVENT, onOpenRequest)
  }, [])

  useEffect(() => {
    if (!active || !enabled) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return
      if (isEditable(e.target)) return
      if (document.querySelector(BLOCKING_SURFACES)) {
        clearSequence()
        return
      }

      if (e.key === "?") {
        clearSequence()
        setOpen(true)
        return
      }

      // Solo letras minúsculas sin Shift: Shift+G o el bloqueo de mayúsculas no arman la secuencia.
      if (e.shiftKey || e.key.length !== 1 || e.key !== e.key.toLowerCase()) {
        clearSequence()
        return
      }

      if (waitingForTarget.current) {
        const target = GO_TARGETS[e.key]
        clearSequence()
        if (target && (isStudent || !target.studentOnly)) {
          e.preventDefault()
          navigate(target.to)
        }
        return
      }

      if (e.key === "g") {
        waitingForTarget.current = true
        timer.current = setTimeout(clearSequence, SEQUENCE_TIMEOUT_MS)
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      clearSequence()
    }
  }, [active, enabled, isStudent, navigate, clearSequence])

  return { open, setOpen, enabled, setEnabled }
}
